import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { PaymentSource } from '../../../constants';
import { useCountry } from '../../../components/CountrySelector';
import { useCart } from '../../useCart';
import { useBraintreeClientToken } from '../../useBraintreeClientToken';
import { tokenizeAffirmCard } from '../../../lib/braintree/tokenizeAffirmCard';
import { PLACE_AFFIRM_ORDER_MUTATION } from './placeAffirmOrderMutation.rest';
import { AffirmCheckoutResponse } from './useAffirmCheckout';

export const usePlaceAffirmOrderMutation = () => {
  const { country } = useCountry();
  const {
    cart: {
      id: cartId,
      attributes: { addressId, giftCode, selectedShippingMethodId, paymentSource, error: cartError }
    }
  } = useCart();
  const hasInvalidCoupon = cartError?.type.name === 'Invalid Coupon';

  const { token: braintreeToken } = useBraintreeClientToken();

  const [mutate, { loading, error }] = useMutation(PLACE_AFFIRM_ORDER_MUTATION);

  const handlePlaceOrder = useCallback(
    async (response: AffirmCheckoutResponse) => {
      if (!addressId) return Promise.reject('placeAffirmOrderMutation: no address selected');
      if (!cartId) return Promise.reject('placeAffirmOrderMutation: cart id not found');
      if (!selectedShippingMethodId)
        return Promise.reject('placeAffirmOrderMutation: no shipping method selected');
      if (paymentSource !== PaymentSource.BraintreeAffirm)
        return Promise.reject('placeAffirmOrderMutation: payment source is not Affirm');
      if (!braintreeToken)
        return Promise.reject('placeAffirmOrderMutation: braintree token not found');

      const nonce = await tokenizeAffirmCard(response, braintreeToken);

      return mutate({
        variables: {
          input: {
            data: {
              type: 'orders',
              attributes: {
                address_id: addressId,
                cart_id: cartId,
                country_code: country.alpha2Code,
                offer_code: !hasInvalidCoupon ? giftCode : null,
                payment_source: paymentSource,
                shipment_method: selectedShippingMethodId,
                payment_summary: {
                  encrypted_card_data: { encrypted_data: nonce },
                  affirm_checkout_token: response.checkout_token,
                  billing_address: {
                    address_line1: response.billing_address.line1,
                    address_line2: response.billing_address.line2,
                    city: response.billing_address.city,
                    state: response.billing_address.state,
                    country_code: country.alpha2Code,
                    postal_code: response.billing_address.zipcode
                  }
                }
              }
            }
          }
        }
      });
    },
    [
      cartId,
      addressId,
      giftCode,
      selectedShippingMethodId,
      paymentSource,
      country,
      hasInvalidCoupon,
      braintreeToken,
      mutate
    ]
  );

  return [handlePlaceOrder, { loading, error }] as const;
};
